import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Building2, Users, Target, Award, ShieldCheck, Globe, History, TrendingUp, Handshake } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const AboutPage = () => {
  const { settings } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'profile';
  const tabsRef = useRef(null);

  useEffect(() => {
    // Scroll to the tabs section if the URL parameter changes
    // This helps when navigating via the Header dropdown while already on the page
    if (tabsRef.current) {
      const yOffset = -100; // Offset for header
      const y = tabsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [tab]);
  
  const stats = [
    { value: '10+', label: 'Years Experience' },
    { value: '5000+', label: 'Products' },
    { value: '1000+', label: 'B2B Clients' },
    { value: '24/7', label: 'Support' }
  ];

  const milestones = [
    { year: '2014', title: 'Company Foundation', desc: 'Grand Palace was established in Dubai with a vision to provide quality construction materials to local contractors.' },
    { year: '2017', title: 'Product Expansion', desc: 'Added dedicated divisions for Plumbing, Electricals, and Safety Equipment, doubling our inventory capacity.' },
    { year: '2020', title: 'Digital Transformation', desc: 'Launched our first B2B digital portal to streamline wholesale ordering during the pandemic.' },
    { year: '2023', title: 'Regional Growth', desc: 'Expanded our supply chain network to serve the broader GCC market and select African countries.' },
    { year: '2024', title: 'Market Leadership', desc: 'Recognized as one of the fastest-growing general trading companies in the UAE with over 1,000 active corporate accounts.' }
  ];

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header Banner */}
      <div className="bg-[#0c1b44] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Grand Palace</h1>
          <p className="text-gray-300 max-w-3xl mx-auto text-lg leading-relaxed">
            Your trusted partner in wholesale and retail distribution. We provide top-tier general trading solutions, specializing in construction materials, electricals, safety gear, and more across the UAE and beyond.
          </p>
        </div>
      </div>

      <div ref={tabsRef} className="container mx-auto px-4 py-12 max-w-6xl">
        <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto bg-gray-100 rounded-xl p-1 mb-12">
            <TabsTrigger value="profile" className="py-3 font-semibold data-[state=active]:bg-white data-[state=active]:text-[#0c1b44] data-[state=active]:shadow-sm rounded-lg transition-all">Company Profile</TabsTrigger>
            <TabsTrigger value="mission" className="py-3 font-semibold data-[state=active]:bg-white data-[state=active]:text-[#0c1b44] data-[state=active]:shadow-sm rounded-lg transition-all">Vision & Mission</TabsTrigger>
            <TabsTrigger value="why-us" className="py-3 font-semibold data-[state=active]:bg-white data-[state=active]:text-[#0c1b44] data-[state=active]:shadow-sm rounded-lg transition-all">Why Choose Us</TabsTrigger>
            <TabsTrigger value="milestones" className="py-3 font-semibold data-[state=active]:bg-white data-[state=active]:text-[#0c1b44] data-[state=active]:shadow-sm rounded-lg transition-all">Milestones</TabsTrigger>
          </TabsList>

          {/* Company Profile Tab */}
          <TabsContent value="profile" className="space-y-8 animate-in fade-in-50 duration-500">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-navy border-l-4 border-gold pl-4">Who We Are</h2>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Grand Palace General Trading L.L.C is a premier B2B and retail supplier based in Dubai, United Arab Emirates. Since our inception, we have built a formidable reputation based on trust, reliability, and an unwavering commitment to quality.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  We understand that in the fast-paced UAE market, timely delivery and dependable materials are the bedrock of any successful project. That's why we stock over 5,000 SKUs across multiple sectors, ensuring that our corporate clients, contractors, and retail customers always have access to exactly what they need, exactly when they need it.
                </p>
                <div className="flex gap-4 pt-4">
                  <div className="flex items-center gap-2 text-navy font-semibold"><Building2 className="text-gold h-5 w-5" /> Headquartered in Dubai</div>
                  <div className="flex items-center gap-2 text-navy font-semibold"><Handshake className="text-gold h-5 w-5" /> Trusted B2B Partner</div>
                </div>
              </div>
              <div className="bg-gray-100 rounded-2xl p-8 relative overflow-hidden h-full min-h-[400px] flex items-center justify-center">
                 <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#0c1b44] via-transparent to-transparent" />
                 <Building2 className="w-48 h-48 text-[#0c1b44]/20 absolute -right-10 -bottom-10" />
                 <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold text-navy mb-4">Our Core Competencies</h3>
                    <ul className="space-y-4 text-left inline-block">
                      <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-gold" /> <span className="font-medium">Bulk Material Sourcing</span></li>
                      <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-gold" /> <span className="font-medium">End-to-End Supply Chain Management</span></li>
                      <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-gold" /> <span className="font-medium">Project Procurement Consulting</span></li>
                      <li className="flex items-center gap-3"><div className="w-2 h-2 rounded-full bg-gold" /> <span className="font-medium">Nationwide Logistics & Delivery</span></li>
                    </ul>
                 </div>
              </div>
            </div>
          </TabsContent>

          {/* Mission & Vision Tab */}
          <TabsContent value="mission" className="space-y-8 animate-in fade-in-50 duration-500">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-0 shadow-lg relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                <div className="absolute top-0 left-0 w-1 h-full bg-[#0c1b44]" />
                <CardContent className="p-10">
                  <div className="w-16 h-16 rounded-xl bg-[#0c1b44]/10 flex items-center justify-center mb-8">
                    <Target className="h-8 w-8 text-[#0c1b44]" />
                  </div>
                  <h2 className="text-3xl font-bold text-navy mb-6">Our Mission</h2>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    To bridge the gap between quality manufacturers and end-users by providing an efficient, reliable, and cost-effective distribution network. We are committed to delivering excellence in every transaction, ensuring our clients receive the best value, and fostering long-term, trust-based partnerships across the industry.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg relative overflow-hidden group hover:-translate-y-1 transition-transform duration-300">
                <div className="absolute top-0 left-0 w-1 h-full bg-gold" />
                <CardContent className="p-10">
                  <div className="w-16 h-16 rounded-xl bg-gold/10 flex items-center justify-center mb-8">
                    <Globe className="h-8 w-8 text-gold" />
                  </div>
                  <h2 className="text-3xl font-bold text-navy mb-6">Our Vision</h2>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    To be the foremost general trading hub in the Middle East, universally recognized for our vast product portfolio, uncompromising quality standards, and innovative B2B supply chain solutions. We aim to empower businesses to build the future by being the most dependable link in their supply chain.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Why Choose Us Tab */}
          <TabsContent value="why-us" className="space-y-12 animate-in fade-in-50 duration-500">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl font-bold text-navy mb-6">The Grand Palace Advantage</h2>
              <p className="text-gray-600 text-lg">
                In the UAE's competitive trading landscape, trust is earned through consistency. Here is why over 1,000 businesses choose us as their primary supplier.
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Building2, title: 'Vast Inventory', desc: 'Over 5,000+ SKUs ready to dispatch immediately, minimizing your project downtime.' },
                { icon: ShieldCheck, title: 'Quality Assurance', desc: '100% genuine products sourced directly from trusted global and local manufacturers.' },
                { icon: Users, title: 'Expert Team', desc: 'Dedicated B2B account managers who understand your industry-specific requirements.' },
                { icon: Award, title: 'Competitive Pricing', desc: 'Market-leading rates and flexible corporate credit terms for long-term partners.' }
              ].map((feature, idx) => (
                <Card key={idx} className="border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 group">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#0c1b44]/5 group-hover:bg-[#0c1b44] group-hover:text-white transition-colors duration-300 flex items-center justify-center mx-auto mb-6">
                      <feature.icon className="h-8 w-8 text-[#0c1b44] group-hover:text-gold transition-colors duration-300" />
                    </div>
                    <h3 className="font-bold text-xl text-navy mb-3">{feature.title}</h3>
                    <p className="text-gray-500 leading-relaxed">{feature.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Milestones Tab */}
          <TabsContent value="milestones" className="space-y-8 animate-in fade-in-50 duration-500">
             <div className="max-w-4xl mx-auto">
               <div className="text-center mb-12">
                 <h2 className="text-3xl font-bold text-navy mb-4">Our Journey</h2>
                 <p className="text-gray-600">A timeline of our growth, driven by our commitment to our clients.</p>
               </div>

               <div className="relative border-l-2 border-[#0c1b44]/20 ml-4 md:ml-1/2 space-y-12 pb-8">
                 {milestones.map((milestone, idx) => (
                   <div key={idx} className="relative pl-8 md:pl-0">
                     {/* Timeline Dot */}
                     <div className="absolute w-4 h-4 bg-gold rounded-full -left-[9px] md:left-1/2 md:-ml-[9px] top-1.5 border-4 border-white shadow-sm z-10" />
                     
                     <div className={`md:w-1/2 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12 md:ml-auto'}`}>
                       <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                         <span className="text-gold font-black text-2xl block mb-2">{milestone.year}</span>
                         <h3 className="text-xl font-bold text-navy mb-2">{milestone.title}</h3>
                         <p className="text-gray-600 leading-relaxed">{milestone.desc}</p>
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
          </TabsContent>
        </Tabs>

        {/* Stats Footer - Visible across all tabs */}
        <div className="bg-[#0c1b44] rounded-2xl p-10 text-white mt-20 shadow-xl relative overflow-hidden">
          <TrendingUp className="absolute -right-10 -bottom-10 w-64 h-64 text-white/5" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative z-10">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-5xl font-black text-gold mb-3">{stat.value}</div>
                <div className="text-sm font-semibold tracking-wider uppercase text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutPage;
